import styled from 'styled-components';
import { Header } from 'semantic-ui-react';
import { useProjectContext } from '~/hooks';
import { usePermissions } from '~/requests/permissions/usePermissions';
import { calculateProjectCosts } from '~/utils/calculators';
import { sizeKwdc, getProjectStorageSizeKwh } from '~/utils/calculators/system';
import { formatDollars } from '~/utils/formatNumber';
import { DealStates, PricingType, UnitDecimals } from '~/constants';

const Container = styled.div`
  * {
    padding: 0;
    margin: 0;
  }

  h3 {
    margin: var(--medium) 0;
  }

  span {
    color: var(--color-primary);
  }
`;

const TextContainer = styled.div`
  max-width: 29.25rem;
  padding: var(--x-small) 0;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
`;

export default function BudgetSummary() {
  const { formattedProject: project } = useProjectContext();
  const { permissions } = usePermissions();

  const bid = project?.acceptedBid ? { bid: project.acceptedBid } : undefined;
  const projectCosts = calculateProjectCosts(project, permissions, bid);

  const isStorageOnly = getProjectStorageSizeKwh(project) > 0 && sizeKwdc(project) === 0;
  const isInDiligenceOrLater = (project?.dealState ?? 0) >= DealStates.IN_DILIGENCE;
  const isEpcStyle = project?.pricingType === PricingType.ACQUISITION || project?.pricingType === PricingType.DEV_FEE;

  // Mirrors `showEpcCosts` in BudgetTable for viewMode='summary': totals are only
  // shown for in-diligence-or-later EPC-style projects, or for OFFTAKE.
  const showTotals = (isInDiligenceOrLater && isEpcStyle) || project?.pricingType === PricingType.OFFTAKE;

  const total = showTotals
    ? formatDollars(projectCosts?.total?.total, { dec: UnitDecimals.TOTAL })
    : 'TBD';
  const unit = showTotals
    ? formatDollars(
      isStorageOnly ? projectCosts?.perKwh?.total : projectCosts?.perWdc?.total,
      { dec: isStorageOnly ? 2 : UnitDecimals.WDC }
    )
    : 'TBD';
  const unitLabel = isStorageOnly ? 'per kWh' : 'per Wdc';

  return (
    <Container>
      <Header as="h3">Budget summary</Header>
      <TextContainer>
        <p>Project budget - <span>{total}</span></p>
        <p>Project budget {unitLabel} - <span>{unit}</span></p>
      </TextContainer>
    </Container>
  );
}
